import { isEmpty, partition } from 'lodash';
import { BoxSkeleton, Divider, Stack, Typography } from '@common-components';
import { PlacementType, SubmissionMethod } from 'enums';
import { messages } from 'i18n';
import { TeamInsuranceProduct } from 'types';
import { demoData } from 'utils';
import DataGridNoResults from 'broker/components/common/QuotesAndPolicies/DataGridNoResults';
import MainScrollArea from 'broker/hoc/MainScrollArea';
import { ProductsProps } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/AddProducts/types';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import ProductsTableHeader from './ProductsTableHeader';
import ProductsTableItem from './ProductsTableItem';

export function ProductsTableView({
  teamProducts,
  selectedProducts,
  onProductSelected,
  onProductRemoved,
  isLoading,
  searchInput,
  bottomTableContent,
  quoteExits,
}: ProductsProps) {
  const { submission } = useSubmissionsWorkspace();
  const isDirectPlacement = submission?.placementType === PlacementType.Direct;
  // Helper function to determine available methods based on product properties
  const getAvailableMethods = (teamProduct: TeamInsuranceProduct) => {
    const availableMethods = [];
    if (teamProduct.insuranceProduct.externalProductId) {
      availableMethods.push(SubmissionMethod.API);
    }
    if (teamProduct.insuranceProduct.submissionPortal) {
      availableMethods.push(SubmissionMethod.Portal);
    }
    if (!isEmpty(teamProduct.contacts)) {
      availableMethods.push(SubmissionMethod.Email);
    }
    return availableMethods;
  };

  // Split the products into two groups: with API availability and without
  const [apiAvailableProducts, nonApiProducts] = partition(teamProducts, (product) => {
    const availableMethods = getAvailableMethods(product);
    return availableMethods.includes(SubmissionMethod.API);
  });

  const renderItems = (productsList: typeof teamProducts) => (
    <Stack
      divider={<Divider />}
      borderLeft={1}
      borderRight={1}
      borderBottom={1}
      borderColor="divider"
      borderRadius=" 0 0 8px 8px"
    >
      {!isEmpty(productsList) ? (
        productsList!.map((product) => (
          <ProductsTableItem
            quoteExits={quoteExits}
            key={product.id}
            teamProduct={product}
            searchInput={searchInput}
            selectedTeamProducts={selectedProducts}
            onProductSelected={onProductSelected}
            onProductRemoved={onProductRemoved}
          />
        ))
      ) : (
        <DataGridNoResults />
      )}
    </Stack>
  );

  const skeleton = (
    <Stack gap={2} p={2}>
      {demoData(10).map((row, index) => (
        <Stack gap={2} key={row.id}>
          <BoxSkeleton isLoading sx={{ width: 1, height: 45 }} index={index} />
          <Divider />
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack overflow="hidden" flex="1 1 0">
      <MainScrollArea id="marketsScrollArea" flex={1} sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {isDirectPlacement ? (
          <Stack m={2}>
            <ProductsTableHeader />
            {isLoading ? skeleton : renderItems(teamProducts)}
          </Stack>
        ) : (
          <>
            <Stack m={2}>
              <Typography variant="subtitle2" mb={1}>
                {messages.submissionWorkspace.addProducts.apiAvailable}
              </Typography>
              <ProductsTableHeader />
              {isLoading ? skeleton : renderItems(apiAvailableProducts)}
            </Stack>

            <Stack m={2}>
              <Typography variant="subtitle2" mb={1}>
                {messages.submissionWorkspace.addProducts.otherProducts}
              </Typography>
              <ProductsTableHeader />
              {isLoading ? skeleton : renderItems(nonApiProducts)}
            </Stack>
          </>
        )}
        {bottomTableContent}
      </MainScrollArea>
    </Stack>
  );
}
